import { useMemo } from "react";

import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";

import { formatAwsDateString, formatTimestamp } from "@covertree/utils";
import { LoadingOrValue } from "@covertree/ui";
import DisplayPropertiesCard from "@covertree/ui/content/DisplayPropertiesCard";

import { useAccessPattern } from "@hooks/useAccessPattern";
import {
  RenewalBaseFragment,
  RenewalStatus,
  RenewalWithCheckReceivalFragment,
} from "@covertree/agent-portal-graphql";
import { mapPaymentSchedule } from "@utils/mapPaymentSchedule";
import { mapPaymentMethod } from "@utils/mapPaymentMethod";

interface Props {
  renewal?: RenewalBaseFragment | RenewalWithCheckReceivalFragment;
  loading: boolean;
}

export function PolicyDetails({ renewal, loading }: Props) {
  const checkReceival = (
    renewal as RenewalWithCheckReceivalFragment | undefined
  )?.checkReceival;

  const { canMarkRenewalAsPaid } = useAccessPattern();

  const isNonRenewalIssued = useMemo(
    () => renewal?.status === RenewalStatus.NonRenewalIssued,
    [renewal]
  );

  return (
    <>
      <Grid item xs={12}>
        <DisplayPropertiesCard
          properties={[
            {
              label: "Policy locator",
              value: (
                <LoadingOrValue
                  loading={!renewal && loading}
                  value={renewal?.policyLocator}
                  height={24}
                  width={121}
                />
              ),
              showAsTypography: true,
            },
            {
              label: "Payment term",
              value: (
                <LoadingOrValue
                  loading={!renewal && loading}
                  value={
                    renewal?.paymentScheduleName
                      ? mapPaymentSchedule(renewal.paymentScheduleName)
                      : ""
                  }
                  height={24}
                  width={121}
                />
              ),
              showAsTypography: true,
            },
            {
              label: "Payment method",
              value: (
                <LoadingOrValue
                  loading={!renewal && loading}
                  value={
                    renewal?.paymentMethod
                      ? mapPaymentMethod(renewal.paymentMethod)
                      : ""
                  }
                  height={24}
                  width={121}
                />
              ),
              showAsTypography: true,
            },
            ...(canMarkRenewalAsPaid && !!checkReceival
              ? [
                  {
                    label: "Settled date",
                    value: checkReceival?.createdTimestamp
                      ? formatTimestamp(checkReceival.createdTimestamp)
                      : "N/A",
                  },
                  {
                    label: "Settled by",
                    value: checkReceival?.createdBy || "N/A",
                    gridSize: 6,
                  },
                  {
                    label: "Invoice settlement description",
                    value: checkReceival?.description || "None",
                    gridSize: 12,
                  },
                ]
              : []),
          ]}
        />
      </Grid>

      {!renewal && loading && (
        <>
          <Grid item xs={12}>
            <Skeleton variant="rounded" height={178} />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant="rounded" height={98} />
          </Grid>
        </>
      )}

      {renewal && !isNonRenewalIssued && (
        <Grid item xs={12}>
          <DisplayPropertiesCard
            properties={[
              {
                label: "Created date",
                value: renewal?.createdDate
                  ? formatAwsDateString(renewal.createdDate)
                  : "N/A",
                gridSize: 6,
              },
              {
                label: "Issue date",
                value: renewal?.issuedDate
                  ? formatAwsDateString(renewal.issuedDate)
                  : "N/A",
                gridSize: 6,
              },
              {
                label: "Start date",
                value: renewal?.renewalTerm.effectiveDate
                  ? formatAwsDateString(renewal.renewalTerm.effectiveDate)
                  : "N/A",
                gridSize: 6,
              },
              {
                label: "Expiration date",
                value: renewal?.renewalTerm.endDate
                  ? formatAwsDateString(renewal.renewalTerm.endDate)
                  : "N/A",
                gridSize: 6,
              },
            ]}
          />
        </Grid>
      )}
    </>
  );
}
